import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  DocumentTextIcon, 
  UserGroupIcon, 
  ScaleIcon,
  LockClosedIcon,
  GlobeAltIcon,
  BellAlertIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const Terms = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerItems = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <motion.div 
        className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 dark:from-blue-900 dark:via-indigo-900 dark:to-purple-900 py-16 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Background pattern and details */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 opacity-20 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-gradient-to-tr from-purple-400 to-indigo-400 opacity-20 blur-3xl"></div>
          <div className="absolute inset-0 opacity-10 bg-grid-pattern"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 relative z-10 text-center">
          <motion.h1 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              Kullanım Şartları ve Koşulları
            </span>
          </motion.h1>
          
          <motion.p 
            className="text-blue-100 max-w-3xl mx-auto text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Bu sayfada Dersmaster platformunun kullanım koşullarını, hak ve yükümlülüklerinizi bulabilirsiniz.
          </motion.p>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Last Updated Section */}
        <motion.div 
          className="mb-12 text-sm text-right text-gray-500 dark:text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          Son Güncelleme: 01 Mayıs 2025
        </motion.div>

        {/* Table of Contents */}
        <motion.section
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="mb-12 bg-white dark:bg-gray-800 rounded-xl shadow-md p-6"
        >
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">İçindekiler</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { id: "giris", title: "Giriş ve Genel Bakış", icon: <DocumentTextIcon className="h-5 w-5" /> },
              { id: "kayit", title: "Kullanıcı Kaydı", icon: <UserGroupIcon className="h-5 w-5" /> },
              { id: "sorumluluklar", title: "Kullanıcı Sorumlulukları", icon: <ShieldCheckIcon className="h-5 w-5" /> },
              { id: "fikri-mulkiyet", title: "Fikri Mülkiyet Hakları", icon: <ScaleIcon className="h-5 w-5" /> },
              { id: "gizlilik", title: "Gizlilik Politikası", icon: <LockClosedIcon className="h-5 w-5" /> },
              { id: "sorumluluk", title: "Sorumluluk Sınırlamaları", icon: <BellAlertIcon className="h-5 w-5" /> },
              { id: "degisiklikler", title: "Hizmet Değişiklikleri", icon: <GlobeAltIcon className="h-5 w-5" /> },
              { id: "iletisim", title: "İletişim Bilgileri", icon: <EnvelopeIcon className="h-5 w-5" /> }
            ].map((item) => (
              <a 
                key={item.id} 
                href={`#${item.id}`} 
                className="flex items-center p-3 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors group"
              >
                <span className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 mr-3">
                  {item.icon}
                </span>
                <span className="text-gray-700 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                  {item.title}
                </span>
              </a>
            ))}
          </div>
        </motion.section>

        {/* Terms of Service Content */}
        <motion.div 
          variants={staggerItems} 
          initial="hidden" 
          animate="visible"
          className="space-y-10"
        >
          {/* Giriş ve Genel Bakış */}
          <motion.section 
            id="giris" 
            variants={fadeIn} 
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:underline"
          >
            <h2 className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              1. Giriş ve Genel Bakış
            </h2>
            
            <p>
              Dersmaster platformuna hoş geldiniz. Bu Kullanım Şartları ve Koşulları ("Şartlar"), Dersmaster web sitesini, mobil uygulamasını ve ilgili hizmetlerini ("Platform") kullanımınızı düzenleyen yasal bağlayıcı bir sözleşmedir. Bu Platform, eğitmenler ile öğrenciler arasında bir öğrenme ve öğretme bağlantısı kurma amacıyla Dersmaster Teknoloji A.Ş. ("Biz", "Bize" veya "Dersmaster") tarafından işletilmektedir.
            </p>
            
            <p>
              Platformumuza erişerek veya kullanarak, bu Şartları kabul ettiğinizi ve bunlara uymayı taahhüt ettiğinizi beyan etmektesiniz. Eğer bu Şartları kabul etmiyorsanız, lütfen Platformumuzu kullanmayı bırakınız.
            </p>
            
            <p>
              Bu Şartlar, Türkiye Cumhuriyeti kanunlarına tabidir ve bu kanunlara uygun olarak yorumlanacaktır.
            </p>
          </motion.section>
          
          {/* Kullanıcı Kaydı */}
          <motion.section 
            id="kayit" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              2. Kullanıcı Kaydı ve Hesap Güvenliği
            </h2>
            
            <p>
              Platformumuza kayıt olmak için, en az 18 yaşında olmanız veya yasal vasinizin gözetimi altında olmanız gerekmektedir. Kayıt sırasında ve Platform kullanımınız süresince doğru, güncel ve eksiksiz bilgiler sağlamakla yükümlüsünüz.
            </p>
            
            <p>
              Hesap güvenliğinizden ve hesabınız altında gerçekleştirilen tüm aktivitelerden siz sorumlusunuz. Şifrenizin veya hesabınızın herhangi bir şekilde yetkisiz kullanımı durumunda, derhal bize bildirmelisiniz.
            </p>
            
            <p>
              <strong>Öğrenci Hesapları:</strong> Öğrenci olarak kaydolduğunuzda, Platformda eğitmenlerden ders talep etme ve alma hakkına sahip olursunuz.
            </p>
            
            <p>
              <strong>Eğitmen Hesapları:</strong> Eğitmen olarak kaydolduğunuzda, belirli doğrulama süreçlerinden geçmeniz ve ilgili doğrulama belgelerini sağlamanız gerekebilir. Eğitmenler, kendi uzmanlık alanlarında ders vermeyi ve öğrencilere kaliteli eğitim hizmeti sunmayı taahhüt ederler.
            </p>
            
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 mt-4">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                <strong>Not:</strong> Dersmaster, herhangi bir zamanda ve herhangi bir nedenle hesapları askıya alma veya sonlandırma hakkını saklı tutar. Bu, Şartları ihlal eden veya Platformumuzu kötü amaçla kullanan hesaplar için geçerlidir.
              </p>
            </div>
          </motion.section>
          
          {/* Kullanıcı Sorumlulukları */}
          <motion.section 
            id="sorumluluklar" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <ShieldCheckIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              3. Kullanıcı Sorumlulukları ve Davranış Kuralları
            </h2>
            
            <p>
              Platformumuzu kullanırken aşağıdaki kurallara uymayı kabul edersiniz:
            </p>
            
            <ul>
              <li>Yasa dışı, zararlı, tehdit edici, taciz edici, karalayıcı, müstehcen veya başka şekilde sakıncalı içerik yayınlamamak</li>
              <li>Başka bir kişi veya kuruluşun kimliğine bürünmemek</li>
              <li>Platform güvenliğini ihlal etmemek veya test etmemek</li>
              <li>Platformdaki diğer kullanıcılara spam göndermemek veya onları taciz etmemek</li>
              <li>Platformdaki özel derslerde profesyonel ve saygılı bir tutum sergilemek</li>
              <li>Eğitmenler için: Derslerde profesyonel standartları korumak ve kaliteli eğitim sunmak</li>
              <li>Öğrenciler için: Derslere zamanında katılmak ve eğitmenlerle saygılı iletişim kurmak</li>
            </ul>
            
            <p>
              Bu kuralları ihlal eden kullanıcılar, hesaplarının askıya alınması veya sonlandırılması dahil olmak üzere çeşitli yaptırımlarla karşılaşabilirler.
            </p>
          </motion.section>
          
          {/* Fikri Mülkiyet Hakları */}
          <motion.section 
            id="fikri-mulkiyet" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <ScaleIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              4. Fikri Mülkiyet Hakları
            </h2>
            
            <p>
              Platform ve içeriğindeki tüm fikri mülkiyet hakları, aksi belirtilmedikçe Dersmaster'a aittir. Dersmaster markası, logosu ve hizmet markaları, Dersmaster'in tescilli ticari markalarıdır. Platformda yayınlanan tüm içerik, telif hakkı, ticari marka ve diğer fikri mülkiyet kanunlarıyla korunmaktadır.
            </p>
            
            <p>
              <strong>Eğitmen İçeriği:</strong> Eğitmenler, kendi oluşturdukları eğitim içeriklerinin fikri mülkiyet haklarını ellerinde tutarlar. Ancak, bu içeriği Platformumuzda paylaşarak, Dersmaster'a bu içeriği kullanma, gösterme ve dağıtma konusunda sınırlı bir lisans vermiş olurlar.
            </p>
            
            <p>
              <strong>Kullanıcı İçeriği:</strong> Platform üzerinde paylaştığınız yorumlar, değerlendirmeler ve diğer içerikler sizin mülkünüzdür. Ancak, bu içeriği Platformumuzda paylaşarak, Dersmaster'a bu içeriği kullanma, gösterme ve dağıtma konusunda dünya çapında, telifsiz, sürekli bir lisans vermiş olursunuz.
            </p>
            
            <p>
              Platformumuzdaki hiçbir içeriği önceden açık yazılı iznimiz olmadan kopyalayamaz, değiştiremez, dağıtamaz veya ticari amaçla kullanamazsınız.
            </p>
          </motion.section>
          
          {/* Gizlilik Politikası */}
          <motion.section 
            id="gizlilik" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <LockClosedIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              5. Gizlilik Politikası
            </h2>
            
            <p>
              Kişisel verilerinizin korunması bizim için önemlidir. Kişisel bilgilerinizin nasıl toplandığı, kullanıldığı ve korunduğu hakkında detaylı bilgi için lütfen <Link to="/privacy" className="text-indigo-600 dark:text-indigo-400 hover:underline">Gizlilik Politikamızı</Link> inceleyiniz.
            </p>
            
            <p>
              Platformumuzu kullanarak, <strong>Kişisel Verilerin Korunması Kanunu (KVKK)</strong> uyarınca kişisel verilerinizin işlenmesine rıza göstermiş olursunuz. Bu rızanızı dilediğiniz zaman geri çekme hakkına sahipsiniz.
            </p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-600 p-4">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Platform kullanımınız sırasında toplanan ve işlenen veriler şunları içerir:
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                <li>Kimlik bilgileri (ad, soyad, e-posta)</li>
                <li>İletişim bilgileri</li>
                <li>Konum bilgileri (şehir, ilçe)</li>
                <li>Ders tercihleriniz ve öğrenim geçmişiniz</li>
                <li>Platform etkileşimleriniz</li>
                <li>Eğitmenler için mesleki deneyim ve uzmanlık alanları</li>
              </ul>
            </div>
          </motion.section>
          
          {/* Sorumluluk Sınırlamaları */}
          <motion.section 
            id="sorumluluk" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <BellAlertIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              6. Sorumluluk Sınırlamaları
            </h2>
            
            <p>
              Dersmaster, Platform üzerindeki içeriklerin doğruluğu, güncelliği veya tamlığı konusunda herhangi bir garanti vermez. Platform, "olduğu gibi" ve "mevcut olduğu şekliyle" sunulmaktadır.
            </p>
            
            <p>
              <strong>Eğitim İçeriği:</strong> Eğitmenler tarafından sağlanan içerik ve verilen derslerin kalitesi eğitmenlerin sorumluluğundadır. Dersmaster, belirli bir eğitim sonucunu garanti edemez ve eğitim içeriğindeki hatalar veya eksikliklerden sorumlu tutulamaz.
            </p>
            
            <p>
              <strong>Ödeme ve İadeler:</strong> Ders ödemeleri, Şartlarımızda ve ilgili hizmet sayfalarında belirtilen politikalara göre yapılır. İade talepleri, ilgili ders sağlayıcının iade politikasına ve ders iptal şartlarına tabidir.
            </p>
            
            <p>
              Dersmaster, hiçbir durumda, doğrudan, dolaylı, tesadüfi, özel veya sonuçsal zararlar dahil olmak üzere herhangi bir zarar için sorumlu tutulamaz. Bu sorumluluk reddi, Dersmaster'in bu tür zararların olasılığından haberdar edilmiş olsa bile geçerlidir.
            </p>
            
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-600 p-4">
              <p className="text-sm text-red-700 dark:text-red-300">
                <strong>Önemli:</strong> Eğitmen ve öğrenciler arasındaki anlaşmazlıklarda Dersmaster aracılık edebilir, ancak nihai sorumluluk taraflara aittir. Dersmaster, kullanıcılar arasındaki herhangi bir anlaşmazlık veya uyuşmazlıktan sorumlu tutulamaz.
              </p>
            </div>
          </motion.section>
          
          {/* Hizmet Değişiklikleri ve Sonlandırma */}
          <motion.section 
            id="degisiklikler" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <GlobeAltIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              7. Hizmet Değişiklikleri ve Sonlandırma
            </h2>
            
            <p>
              Dersmaster, önceden bildirimde bulunmaksızın herhangi bir zamanda Platformdaki hizmetleri değiştirme, askıya alma veya sonlandırma hakkını saklı tutar.
            </p>
            
            <p>
              <strong>Şartlarda Değişiklik:</strong> Bu Şartlar, zaman zaman güncellenebilir. Önemli değişiklikler olması durumunda, değişiklikler yürürlüğe girmeden önce makul bir süre içinde bildirimde bulunacağız. Platformu kullanmaya devam etmeniz, değiştirilen Şartları kabul ettiğiniz anlamına gelecektir.
            </p>
            
            <p>
              <strong>Hesap Sonlandırma:</strong> Bu Şartları ihlal ettiğiniz takdirde, hesabınızı önceden bildirmeksizin askıya alma veya sonlandırma hakkımız saklıdır. Ayrıca, dilediğiniz zaman hesabınızı kapatabilirsiniz.
            </p>
            
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-400 dark:border-emerald-600 p-4 mt-4">
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                <strong>Bilgi:</strong> Hesabınız sonlandırıldığında, hesap bilgileriniz ve içeriğiniz, gizlilik politikamıza uygun olarak silinecek veya anonimleştirilecektir. Ancak, yasal gerekliliklere uymak için bazı bilgileri saklama hakkımız saklıdır.
              </p>
            </div>
          </motion.section>
          
          {/* İletişim Bilgileri */}
          <motion.section 
            id="iletisim" 
            variants={fadeIn}
            className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-800 dark:prose-headings:text-white prose-p:text-gray-600 dark:prose-p:text-gray-300"
          >
            <h2 className="flex items-center">
              <EnvelopeIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-500 mr-3" />
              8. İletişim Bilgileri
            </h2>
            
            <p>
              Bu Şartlar veya Platformumuzla ilgili sorularınız veya endişeleriniz varsa, lütfen bizimle aşağıdaki kanallardan iletişime geçin:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 not-prose mt-6">
              <div className="bg-white dark:bg-gray-800 shadow-md rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">E-posta İletişim</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-2">Genel Sorular ve Destek:</p>
                <a href="mailto:info@dersmaster.com" className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline">info@dersmaster.com</a>
                
                <p className="text-gray-600 dark:text-gray-300 mt-4 mb-2">Hukuk ve Telif Hakkı Sorunları:</p>
                <a href="mailto:info@dersmaster.com" className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline">info@dersmaster.com</a>
              </div>
              
             
            </div>
            
            <div className="mt-8 p-6 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl not-prose">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Yasal Uyarı</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Bu Kullanım Şartları, Türkiye Cumhuriyeti kanunlarına göre yorumlanacak ve uygulanacaktır. Bu Şartlarla ilgili herhangi bir anlaşmazlık durumunda, İstanbul Mahkemeleri ve İcra Daireleri yetkili olacaktır.
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                Şartların İngilizce ve Türkçe versiyonları arasında tutarsızlık olması durumunda, Türkçe versiyon geçerli olacaktır.
              </p>
            </div>
          </motion.section>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;
